import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Icon } from '../icons';
import Select from './Select';
import SearchInput from './SearchInput';
import Pagination, { usePagination, useFitPageSize } from './Pagination';
import SortableTh, { useSort, sortRows } from './SortableTh';
import HelpTip from './HelpTip';
import KpiGrid from './KpiGrid';
import Drawer from './Drawer';
import Modal from './Modal';
import { useConfirm } from './ConfirmProvider';
import { useAsyncAction } from '../hooks/useAsyncAction';

const api = (path, opts = {}) =>
  fetch(path, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...opts });

const fmt = (d) => (d ? new Date(d).toLocaleString('it-IT') : '—');

const ROLE_LABELS = {
  superadmin: 'Superadmin',
  amministratore: 'Amministratore',
  sviluppatore: 'Sviluppatore',
  tecnico: 'Tecnico',
  rivenditore: 'Rivenditore',
};
const ROLE_BADGE = {
  superadmin: 'badge-danger',
  amministratore: 'badge-warn',
  sviluppatore: 'badge-info',
  tecnico: 'badge-info',
  rivenditore: 'badge-muted',
};
const PRIVILEGED = ['amministratore', 'superadmin'];
// Ruoli assegnabili da questo portale: NON coincide con server/roles.js.
// 'rivenditore' resta un valore valido lato DB/API (README: "opera solo
// sull'app desktop, nessuna UI/endpoint sul portale, resta nell'enum per
// completezza") ma non deve comparire come opzione qui — per questo e'
// escluso da queste due liste pur restando in ROLE_LABELS/ROLE_BADGE, cosi'
// un eventuale utente con quel ruolo (creato fuori da questo portale) resta
// comunque leggibile in tabella invece di mostrare la label grezza.
const ALL_ROLES = ['amministratore', 'tecnico', 'sviluppatore', 'superadmin'];
const MANAGEABLE_ROLES = ['tecnico', 'sviluppatore'];
const USERNAME_RE = /^[a-zA-Z0-9._-]{3,32}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const USER_ACCESSORS = {
  username: { get: (u) => u.username, type: 'string' },
  email: { get: (u) => u.email || '', type: 'string' },
  role: { get: (u) => u.role, type: 'string' },
  active: { get: (u) => (u.active ? 1 : 0), type: 'number' },
};

// Validazione email speculare al server. L'email e' OBBLIGATORIA: vuoto = errore.
// Ritorna la stringa d'errore o null.
function emailError(email) {
  const e = (email || '').trim();
  if (!e) return 'Email obbligatoria';
  if (e.length > 254 || !EMAIL_RE.test(e)) return 'Email non valida';
  return null;
}

// Validazione password speculare a quella del server (users.js / auth.js):
// min 8 caratteri, diversa dallo username. Ritorna la stringa d'errore o null.
function passwordError(pwd, username) {
  if ((pwd || '').length < 8) return 'La password deve avere almeno 8 caratteri';
  if (pwd === username) return 'La password non puo\' essere uguale allo username';
  return null;
}

// --- Stili specifici del pannello, iniettati una volta (come HelpTip/Modal) ---
const STYLE_ID = 'users-panel-styles';
const CSS = `
.pw-field { position: relative; display: flex; align-items: center; }
.pw-field .input { width: 100%; padding-right: 2.4rem; }
.pw-field .pw-toggle { position: absolute; right: .25rem; top: 50%; transform: translateY(-50%); }
.role-select { min-width: 150px; }
.cred-box { display: flex; align-items: center; gap: .75rem; margin-top: 1rem; padding: .7rem .85rem;
  border: 1px dashed var(--hiplaas-orange); border-radius: var(--radius-sm); background: var(--glass-brand); }
.cred-box .cred-main { flex: 1; min-width: 0; }
.cred-box .cred-label { font-size: .7rem; text-transform: uppercase; letter-spacing: .04em; color: var(--text-muted); margin-bottom: .15rem; }
.cred-box code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: .95rem; font-weight: 600; color: var(--text-primary); word-break: break-all; }
.cred-hint { font-size: .8rem; color: var(--text-muted); margin-top: .75rem; }
.user-you { font-size: .72rem; color: var(--text-muted); font-weight: 400; }
.modal-body .field { margin-bottom: .9rem; }
`;
function ensureStyles() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = CSS;
  document.head.appendChild(s);
}

const Empty = ({ icon, text }) => (
  <div className="empty"><Icon name={icon} size={30} /><span>{text}</span></div>
);

// Campo password con toggle mostra/nascondi: l'operatore puo' rileggere e
// correggere quello che ha scritto senza doverlo ridigitare.
function PasswordField({ id, value, onChange, autoFocus }) {
  const [show, setShow] = useState(false);
  return (
    <div className="pw-field">
      <input
        id={id}
        className="input"
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="new-password"
        placeholder="Almeno 8 caratteri"
        autoFocus={autoFocus}
      />
      <button
        type="button"
        className="icon-btn pw-toggle"
        aria-label={show ? 'Nascondi password' : 'Mostra password'}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setShow((s) => !s)}
      >
        <Icon name={show ? 'eyeOff' : 'eye'} size={16} />
      </button>
    </div>
  );
}

// Riquadro che mostra UNA sola volta la password provvisoria generata dal
// sistema, con pulsante "copia".
function CredBox({ cred }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(cred.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard non disponibile: l'operatore la copia a mano */ }
  };
  return (
    <div className="cred-box">
      <div className="cred-main">
        <div className="cred-label">Password provvisoria — {cred.username}</div>
        <code>{cred.password}</code>
      </div>
      <button type="button" className="btn btn-ghost btn-sm" onClick={copy}>
        <Icon name={copied ? 'check' : 'copy'} size={14} />{copied ? 'Copiata' : 'Copia'}
      </button>
    </div>
  );
}

const PW_MODE_OPTIONS = [
  { value: 'auto', label: 'Genera automaticamente' },
  { value: 'manual', label: 'Imposta manualmente' },
];

function UsersPanel({ currentUser }) {
  useEffect(ensureStyles, []);
  const confirm = useConfirm();
  const isSuper = currentUser.role === 'superadmin';
  const roleChoices = useMemo(() => (isSuper ? ALL_ROLES : MANAGEABLE_ROLES), [isSuper]);
  const roleOptions = useMemo(() => roleChoices.map((r) => ({ value: r, label: ROLE_LABELS[r] })), [roleChoices]);
  // L'amministratore puo' gestire solo utenti con ruolo non privilegiato.
  const canManage = useCallback((u) => isSuper || !PRIVILEGED.includes(u.role), [isSuper]);

  const [users, setUsers] = useState([]);
  const [message, setMessage] = useState(null); // esito creazione (sotto il form)
  const [notice, setNotice] = useState(null);   // esito azioni sulla tabella

  // Form "nuovo utente".
  const [newUsername, setNewUsername] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState(roleChoices[0]);
  const [newMode, setNewMode] = useState('auto');
  const [newPassword, setNewPassword] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdCred, setCreatedCred] = useState(null);
  const [createUserOpen, setCreateUserOpen] = useState(false); // drawer "Nuovo utente"
  const [detailUserId, setDetailUserId] = useState(null); // drawer dettaglio (click riga)

  // Modale modifica email (necessaria per il recupero password self-service).
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailValue, setEmailValue] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailErr, setEmailErr] = useState('');

  // Filtri / ordinamento / paginazione.
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [colSort, onColSort] = useSort();

  // Modale reset password.
  const [resetTarget, setResetTarget] = useState(null);
  const [resetMode, setResetMode] = useState('auto');
  const [resetPassword, setResetPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetCred, setResetCred] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api('/api/users');
      if (res.ok) setUsers(await res.json());
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Derivato (non uno snapshot): dopo una modifica dal drawer stesso (ruolo,
  // email, reset, disabilita...) load() aggiorna `users` e il drawer aperto
  // mostra subito il dato nuovo invece di restare fermo a quello del click.
  const detailUser = useMemo(() => users.find((u) => u.id === detailUserId) || null, [users, detailUserId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) =>
      (!roleFilter || u.role === roleFilter) &&
      (!statusFilter || (statusFilter === 'active' ? u.active : !u.active)) &&
      (!q || u.username.toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q) || (ROLE_LABELS[u.role] || u.role).toLowerCase().includes(q))
    );
  }, [users, search, roleFilter, statusFilter]);

  const sorted = useMemo(() => sortRows(filtered, colSort, USER_ACCESSORS), [filtered, colSort]);
  const pagination = usePagination(sorted.length, [search, roleFilter, statusFilter, colSort], 6);
  const paged = sorted.slice(pagination.start, pagination.end);

  // KPI del pannello utenti (sull'intero elenco, non sul filtro).
  const userStats = useMemo(() => ({
    total: users.length,
    active: users.filter((u) => u.active).length,
    disabled: users.filter((u) => !u.active).length,
    tempPw: users.filter((u) => u.must_change_password).length,
  }), [users]);

  // Fit-to-screen come la tabella Licenze: la lista utenti non fa mai scrollare
  // la pagina, si adatta e pagina. L'observer nel hook ricalcola anche quando
  // il form "Nuovo utente" o un avviso sopra la card ne spostano il bordo.
  const userCardRef = useRef(null);
  const userWrapRef = useRef(null);
  const userTheadRef = useRef(null);
  useFitPageSize(
    { cardRef: userCardRef, wrapRef: userWrapRef, theadRef: userTheadRef, bottomGap: 32 },
    pagination.pageSize, pagination.setPageSize,
    users.length > 0
  );

  const submitCreate = async (e) => {
    e.preventDefault();
    setMessage(null);
    setCreatedCred(null);
    const uname = newUsername.trim();
    if (!USERNAME_RE.test(uname)) {
      setMessage({ type: 'error', text: 'Username non valido (3-32 caratteri: lettere, numeri, . _ -)' });
      return;
    }
    const emErr = emailError(newEmail);
    if (emErr) { setMessage({ type: 'error', text: emErr }); return; }
    if (newMode === 'manual') {
      const err = passwordError(newPassword, uname);
      // In caso di errore NON si svuota il campo: l'operatore corregge quanto scritto.
      if (err) { setMessage({ type: 'error', text: err }); return; }
    }
    setCreating(true);
    try {
      const body = { username: uname, role: newRole, mode: newMode };
      const email = newEmail.trim();
      if (email) body.email = email;
      if (newMode === 'manual') body.password = newPassword;
      const res = await api('/api/users', { method: 'POST', body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) { setMessage({ type: 'error', text: data.error || 'Errore' }); return; }
      setMessage({ type: 'success', text: `Utente "${uname}" creato.` });
      if (data.generated_password) setCreatedCred({ username: uname, password: data.generated_password });
      setNewUsername('');
      setNewEmail('');
      setNewPassword('');
      setNewMode('auto');
      setNewRole(roleChoices[0]);
      load();
    } catch {
      setMessage({ type: 'error', text: 'Impossibile contattare il server' });
    } finally {
      setCreating(false);
    }
  };

  const changeRole = async (u, role) => {
    if (role === u.role) return;
    setNotice(null);
    const res = await api(`/api/users/${u.id}`, { method: 'PATCH', body: JSON.stringify({ role }) });
    const data = await res.json();
    if (!res.ok) { setNotice({ type: 'error', text: data.error || 'Errore' }); return; }
    setNotice({ type: 'success', text: `Ruolo di "${u.username}" aggiornato a ${ROLE_LABELS[role] || role}.` });
    load();
  };
  const { run: runChangeRole } = useAsyncAction(changeRole);

  const setActive = async (u, active) => {
    if (!(await confirm({
      title: active ? 'Riabilita utente' : 'Disabilita utente',
      message: active
        ? `Riabilitare l'utente "${u.username}"?\n\nPotrà di nuovo accedere al pannello.`
        : `Disabilitare l'utente "${u.username}"?\n\nNon potrà più effettuare il login. I dati restano e potrai riabilitarlo in seguito.`,
      confirmLabel: active ? 'Riabilita' : 'Disabilita',
      tone: active ? 'primary' : 'warn',
      icon: active ? 'check' : 'ban',
    }))) return;
    setNotice(null);
    const res = await api(`/api/users/${u.id}`, { method: 'PATCH', body: JSON.stringify({ active }) });
    const data = await res.json();
    if (!res.ok) { setNotice({ type: 'error', text: data.error || 'Errore' }); return; }
    setNotice({ type: 'success', text: `Utente "${u.username}" ${active ? 'riabilitato' : 'disabilitato'}.` });
    load();
  };
  const { run: runSetActive, pending: settingActive } = useAsyncAction(setActive);

  const hardDelete = async (u) => {
    if (!(await confirm({
      title: 'Elimina definitivamente',
      message: `ELIMINARE DEFINITIVAMENTE l'utente "${u.username}"?\n\nL'operazione non è reversibile. In alternativa puoi disabilitarlo (mantiene lo storico).`,
      confirmLabel: 'Elimina definitivamente',
      tone: 'danger',
      icon: 'trash',
    }))) return;
    setNotice(null);
    const res = await api(`/api/users/${u.id}?hard=true`, { method: 'DELETE' });
    const data = await res.json();
    if (!res.ok) { setNotice({ type: 'error', text: data.error || 'Errore' }); return; }
    setNotice({ type: 'success', text: `Utente "${u.username}" eliminato definitivamente.` });
    load();
  };
  const { run: runHardDelete, pending: hardDeleting } = useAsyncAction(hardDelete);

  const openReset = (u) => {
    setResetTarget(u);
    setResetMode('auto');
    setResetPassword('');
    setResetError('');
    setResetCred(null);
  };

  const openEmail = (u) => {
    setEmailTarget(u);
    setEmailValue(u.email || '');
    setEmailErr('');
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setEmailErr('');
    const err = emailError(emailValue);
    if (err) { setEmailErr(err); return; }
    setEmailSaving(true);
    try {
      // Stringa vuota => azzera l'email (il server la interpreta come null).
      const res = await api(`/api/users/${emailTarget.id}`, { method: 'PATCH', body: JSON.stringify({ email: emailValue.trim() }) });
      const data = await res.json();
      if (!res.ok) { setEmailErr(data.error || 'Errore'); return; }
      setNotice({ type: 'success', text: `Email di "${emailTarget.username}" aggiornata.` });
      setEmailTarget(null);
      load();
    } catch {
      setEmailErr('Impossibile contattare il server');
    } finally {
      setEmailSaving(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setResetError('');
    if (resetMode === 'manual') {
      const err = passwordError(resetPassword, resetTarget.username);
      if (err) { setResetError(err); return; } // il campo resta compilato
    }
    setResetting(true);
    try {
      const body = { mode: resetMode };
      if (resetMode === 'manual') body.password = resetPassword;
      const res = await api(`/api/users/${resetTarget.id}/reset-password`, { method: 'POST', body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) { setResetError(data.error || 'Errore'); return; }
      if (data.generated_password) {
        setResetCred({ username: resetTarget.username, password: data.generated_password });
      } else {
        setNotice({ type: 'success', text: `Password di "${resetTarget.username}" reimpostata.` });
        setResetTarget(null);
      }
      load();
    } catch {
      setResetError('Impossibile contattare il server');
    } finally {
      setResetting(false);
    }
  };

  const toggleUserStatus = (v) => setStatusFilter((cur) => (cur === v ? '' : v));
  const userKpis = [
    { key: 'total', tone: 'brand', icon: 'users', value: userStats.total, label: 'Totale utenti' },
    { key: 'active', tone: 'success', icon: 'check', value: userStats.active, label: 'Attivi', onClick: () => toggleUserStatus('active'), active: statusFilter === 'active' },
    { key: 'disabled', tone: 'danger', icon: 'ban', value: userStats.disabled, label: 'Disabilitati', onClick: () => toggleUserStatus('disabled'), active: statusFilter === 'disabled' },
    { key: 'tempPw', tone: 'warn', icon: 'key', value: userStats.tempPw, label: 'Password provvisoria' },
  ];
  const openCreateUser = () => { setMessage(null); setCreatedCred(null); setCreateUserOpen(true); };

  return (
    <>
      <KpiGrid items={userKpis} />
      {notice && <div className={`msg msg-${notice.type}`} style={{ marginBottom: '1rem' }}><Icon name={notice.type === 'success' ? 'check' : 'alert'} size={16} />{notice.text}</div>}
      <div className="card card--fit" ref={userCardRef}>
        <div className="card-head card-head--stacked">
          <h2><Icon name="users" size={18} />Utenti del pannello<HelpTip text="Elenco degli account che accedono al pannello. Da qui modifichi il ruolo, disabiliti (l'utente non potra' piu' entrare ma lo storico resta) o resetti la password. La cancellazione definitiva e' riservata al superadmin." /></h2>
          <div className="table-toolbar">
            <SearchInput id="userSearch" placeholder="Cerca username, ruolo…" value={search} onChange={(e) => setSearch(e.target.value)} />
            <Select id="uRole" className={roleFilter ? 'select-wrap--active' : ''} ariaLabel="Filtra per ruolo" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} options={[{ value: '', label: 'Tutti i ruoli' }, ...ALL_ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] }))]} />
            <Select id="uStatus" className={statusFilter ? 'select-wrap--active' : ''} ariaLabel="Filtra per stato" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} options={[{ value: '', label: 'Tutti gli stati' }, { value: 'active', label: 'Attivi' }, { value: 'disabled', label: 'Disabilitati' }]} />
            <button type="button" className="btn btn-accent" style={{ marginLeft: 'auto' }} onClick={openCreateUser}><Icon name="plus" size={16} />Nuovo utente</button>
          </div>
        </div>
        {users.length === 0 ? <Empty icon="users" text="Nessun utente" /> : filtered.length === 0 ? <Empty icon="search" text="Nessun risultato per i filtri applicati" /> : (
            <div className="table-wrap" ref={userWrapRef}>
              <table className="table">
                <thead ref={userTheadRef}>
                  <tr>
                    <SortableTh label="Username" sortKey="username" sort={colSort} onSort={onColSort} className="u-col-username" style={{ width: '14%' }} />
                    <SortableTh label="Email" sortKey="email" sort={colSort} onSort={onColSort} className="u-col-email" style={{ width: '23%' }} />
                    <SortableTh label="Ruolo" sortKey="role" sort={colSort} onSort={onColSort} className="u-col-ruolo" style={{ width: '17%' }} />
                    <SortableTh label="Stato" sortKey="active" sort={colSort} onSort={onColSort} className="u-col-stato" style={{ width: '18%' }} />
                    <th className="u-col-azioni" style={{ width: '23%' }}>Azioni</th>
                    <th className="u-col-chevron" style={{ width: '5%' }} aria-label="Dettaglio"></th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((u) => {
                    const self = u.username === currentUser.username;
                    const manage = canManage(u);
                    return (
                      <tr key={u.id} className="user-row" onClick={() => setDetailUserId(u.id)}>
                        <td className="mono strong">{u.username}{self && <span className="user-you"> (tu)</span>}</td>
                        <td>
                          {u.email
                            ? <span className="mono" style={{ fontSize: '.82rem', wordBreak: 'break-all' }}>{u.email}</span>
                            : <span className="user-you">Nessuna</span>}
                        </td>
                        <td>
                          <span className={`badge ${ROLE_BADGE[u.role] || 'badge-muted'}`}>{ROLE_LABELS[u.role] || u.role}</span>
                        </td>
                        <td>
                          {u.active ? <span className="badge badge-success">Attivo</span> : <span className="badge badge-danger">Disabilitato</span>}
                          {u.must_change_password && <span className="badge badge-warn" style={{ marginLeft: '.35rem' }}>Psw prov.</span>}
                        </td>
                        <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                          {self ? <span className="user-you">Il tuo account</span> : manage ? (
                            <>
                              <button className="btn btn-primary btn-sm" onClick={() => openReset(u)}><Icon name="key" size={14} /><span className="lap-full">Reset password</span><span className="lap-short">Reset psw</span></button>
                              {u.active
                                ? <button className="btn btn-warn btn-sm" disabled={settingActive} onClick={() => runSetActive(u, false)}><Icon name="ban" size={14} />Disabilita</button>
                                : <button className="btn btn-primary btn-sm" disabled={settingActive} onClick={() => runSetActive(u, true)}><Icon name="check" size={14} />Riabilita</button>}
                            </>
                          ) : <span className="user-you">—</span>}
                        </td>
                        <td className="user-row-open" aria-label="Apri dettaglio"><Icon name="chevronRight" size={15} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
        )}
        <Pagination idPrefix="userPage" total={sorted.length} {...pagination} onPageChange={pagination.setPage} onPageSizeChange={pagination.setPageSize} itemLabel="utenti" showSizeSelector={false} />
      </div>

      {createUserOpen && (
        <Drawer title="Nuovo utente" icon="plus" onClose={() => setCreateUserOpen(false)}>
          <form className="drawer-form" onSubmit={submitCreate}>
            <div className="field">
              <label htmlFor="nu-name">Username</label>
              <input id="nu-name" className="input" value={newUsername} onChange={(e) => setNewUsername(e.target.value)} placeholder="es. mario.rossi" autoComplete="off" />
            </div>
            <div className="field">
              <label htmlFor="nu-email">Email <span className="user-you">(obbligatoria — per il recupero password)</span></label>
              <input id="nu-email" className="input" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="es. mario@azienda.it" autoComplete="off" required />
            </div>
            <div className="field">
              <label htmlFor="nu-role">Ruolo</label>
              <Select id="nu-role" ariaLabel="Ruolo" value={newRole} onChange={(e) => setNewRole(e.target.value)} options={roleOptions} />
            </div>
            <div className="field">
              <label htmlFor="nu-mode">Password</label>
              <Select id="nu-mode" ariaLabel="Modalita' password" value={newMode} onChange={(e) => setNewMode(e.target.value)} options={PW_MODE_OPTIONS} />
            </div>
            {newMode === 'manual' && (
              <div className="field">
                <label htmlFor="nu-pw">Password provvisoria</label>
                <PasswordField id="nu-pw" value={newPassword} onChange={setNewPassword} />
              </div>
            )}
            <button className="btn btn-accent" type="submit" disabled={creating || !newUsername.trim() || !newEmail.trim()}>
              <Icon name="plus" size={16} />{creating ? 'Creazione…' : 'Crea utente'}
            </button>
          </form>
          {message && <div className={`msg msg-${message.type}`} style={{ marginTop: '1rem' }}><Icon name={message.type === 'success' ? 'check' : 'alert'} size={16} />{message.text}</div>}
          {createdCred && <CredBox cred={createdCred} />}
        </Drawer>
      )}

      {detailUser && (() => {
        const detailSelf = detailUser.username === currentUser.username;
        const detailManage = canManage(detailUser) && !detailSelf;
        return (
        <Drawer title={detailUser.username} icon="users" onClose={() => setDetailUserId(null)}>
          <div className="audit-detail-panel">
            <div className="audit-detail-title">
              <span className="strong">{detailUser.username}</span>
              {detailSelf && <span className="user-you">(tu)</span>}
              {detailUser.active ? <span className="badge badge-success">Attivo</span> : <span className="badge badge-danger">Disabilitato</span>}
            </div>
            <div className="audit-detail-block">
              <span className="audit-detail-k">Ruolo</span>
              <span className="audit-detail-v">
                {detailManage ? (
                  <Select id={`role-${detailUser.id}`} ariaLabel={`Ruolo di ${detailUser.username}`} value={detailUser.role} onChange={(e) => runChangeRole(detailUser, e.target.value)} options={roleOptions} className="role-select" />
                ) : (
                  <span className={`badge ${ROLE_BADGE[detailUser.role] || 'badge-muted'}`}>{ROLE_LABELS[detailUser.role] || detailUser.role}</span>
                )}
              </span>
            </div>
            <div className="audit-detail-block">
              <span className="audit-detail-k">Email</span>
              <span className="audit-detail-v">
                {detailUser.email || <span className="user-you">Nessuna</span>}
                {detailManage && (
                  <button type="button" className="link-btn" style={{ marginLeft: '.5rem', fontSize: '.78rem' }} onClick={() => openEmail(detailUser)}>{detailUser.email ? 'Modifica' : 'Aggiungi'}</button>
                )}
              </span>
            </div>
            <div className="audit-detail-block">
              <span className="audit-detail-k">Password</span>
              <span className="audit-detail-v">{detailUser.must_change_password ? <span className="badge badge-warn">Provvisoria — da cambiare</span> : 'Impostata dall\'utente'}</span>
            </div>
            <div className="audit-detail-block">
              <span className="audit-detail-k">Creato</span>
              <span className="audit-detail-v">{fmt(detailUser.created_at)}</span>
            </div>
            <div className="audit-detail-block">
              <span className="audit-detail-k">ID</span>
              <span className="audit-detail-v mono">#{detailUser.id}</span>
            </div>
            {detailManage && isSuper && (
              <div className="audit-detail-danger">
                <button type="button" className="btn btn-danger btn-block" disabled={hardDeleting} onClick={() => runHardDelete(detailUser)}>
                  <Icon name="trash" size={14} />{hardDeleting ? 'Eliminazione…' : 'Elimina definitivamente'}
                </button>
              </div>
            )}
          </div>
        </Drawer>
        );
      })()}

      {resetTarget && (
        <Modal title={`Reset password — ${resetTarget.username}`} icon="key" onClose={() => setResetTarget(null)}>
          {resetCred ? (
            <div className="modal-body">
              <p>Nuova password provvisoria generata. L'utente dovra' cambiarla al primo accesso.</p>
              <CredBox cred={resetCred} />
              <p className="cred-hint">Comunicala all'utente: non sara' piu' visibile dopo la chiusura.</p>
              <div className="modal-actions">
                <button type="button" className="btn btn-primary" onClick={() => setResetTarget(null)}>Chiudi</button>
              </div>
            </div>
          ) : (
            <form className="modal-body" onSubmit={submitReset}>
              {resetError && <div className="msg msg-error" style={{ marginBottom: '.9rem' }}><Icon name="alert" size={16} />{resetError}</div>}
              <div className="field">
                <label htmlFor="rs-mode">Modalita'</label>
                <Select id="rs-mode" ariaLabel="Modalita' password" value={resetMode} onChange={(e) => setResetMode(e.target.value)} options={PW_MODE_OPTIONS} />
              </div>
              {resetMode === 'manual' && (
                <div className="field">
                  <label htmlFor="rs-pw">Nuova password provvisoria</label>
                  <PasswordField id="rs-pw" value={resetPassword} onChange={setResetPassword} autoFocus />
                </div>
              )}
              <p className="cred-hint">L'utente sara' obbligato a cambiarla al primo accesso.</p>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setResetTarget(null)}>Annulla</button>
                <button type="submit" className="btn btn-primary" disabled={resetting}>{resetting ? 'Reimpostazione…' : 'Reimposta password'}</button>
              </div>
            </form>
          )}
        </Modal>
      )}

      {emailTarget && (
        <Modal title={`Email — ${emailTarget.username}`} icon="mail" onClose={() => setEmailTarget(null)}>
          <form className="modal-body" onSubmit={submitEmail}>
            {emailErr && <div className="msg msg-error" style={{ marginBottom: '.9rem' }}><Icon name="alert" size={16} />{emailErr}</div>}
            <div className="field">
              <label htmlFor="ue-email">Indirizzo email</label>
              <input id="ue-email" className="input" type="email" value={emailValue} onChange={(e) => setEmailValue(e.target.value)} placeholder="es. mario@azienda.it" autoComplete="off" required autoFocus />
            </div>
            <p className="cred-hint">Obbligatoria: serve per il recupero password self-service.</p>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setEmailTarget(null)}>Annulla</button>
              <button type="submit" className="btn btn-primary" disabled={emailSaving || !emailValue.trim()}>{emailSaving ? 'Salvataggio…' : 'Salva email'}</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}

export default UsersPanel;
